import { StatusBadge } from "@solomon-apps/shared/src/components/StatusBadge";
import {
    type HeaderTag,
    ProgramActiveHeader
} from "@solomon-apps/shared/src/widgets/yaas/programs/ProgramActiveHeader";
import React from "react";

import type { Program } from "../../shared/programMocks";
import type { FundingAccount } from "../fundingMocks";
import { regionLabelOf, STATUS_STATE, STATUS_TEXT } from "../programHelpers";
import { ProgramNameField } from "../ProgramNameField";

export interface ProgramHeaderProps {
    /** The program the header describes. `program.status` drives the mode. */
    program: Program;
    /** Draft name value (controlled input) — draft mode only. */
    name: string;
    /** Fired on each keystroke of the draft name input. */
    onChangeName: (next: string) => void;
    /** Selected regions — rendered as region Badges in the active tags row. */
    regions: string[];
    /** All funding accounts — filtered to this program for the sand Badges. */
    fundingAccounts: FundingAccount[];
}

/**
 * ProgramHeader — the mode-aware header **Container** at the top of the program
 * detail view, above `ProgramKeyStats`. It owns the create/view fork for the
 * title block on `program.status` and the domain → status/label/tone/tag
 * mapping:
 *
 * - **draft** (`in_progress`): the editable `ProgramNameField` + `StatusBadge`.
 * - **active / ended**: delegates to the shared `ProgramActiveHeader` View,
 *   forwarding the resolved `name` / status / tag chips / `nextDistribution`.
 *
 * The active branch renders the SAME `ProgramActiveHeader` the Viewing-Program
 * sim uses (`packages/shared/src/widgets/yaas/programs`) — one active header,
 * real app + sims identical, so all its layout fixes (container-responsive
 * `@container/hdr` stacking, the compact `min-w-[228px] max-w-[236px]` meter
 * slot, the `whitespace-nowrap` title, the `shrink-0` left block) apply to the
 * real app with no future divergence. This Container still owns the
 * create/view fork and the domain → status/tone/tag mapping (via
 * `programHelpers`) and forwards the resolved values; the sim seeds them from
 * fixtures instead.
 *
 * @see ProgramHeader.intent.md   policy + recipes
 * @see ProgramHeader.layout.html intended layout
 * @see packages/shared/src/widgets/yaas/programs/ProgramActiveHeader.tsx
 */
export function ProgramHeader({
    program,
    name,
    onChangeName,
    regions,
    fundingAccounts
}: ProgramHeaderProps): React.ReactElement {
    // "isActive" means non-draft (active OR ended) → read-only header. Only
    // drafts (in_progress) get the editable name field.
    const isActive = program.status !== "in_progress";

    if (isActive) {
        // Resolve the domain data into the shared View's pre-resolved prop
        // shape: the target chip (septenary), then region chips (default),
        // then this program's funding-account chips (sand).
        const tags: HeaderTag[] = [
            // Who the program rewards — read-only target chip so active /
            // previously-active programs surface it (the editable Target toggle
            // is draft-only in Configuration). Leads the row, septenary-toned.
            { label: "Users", tone: "septenary" },
            ...regions.map((r) => ({
                label: regionLabelOf(r),
                tone: "default" as const
            })),
            // Funding account(s) backing this program — sand chips so they read
            // distinctly from the region chips. Active / previously-active only.
            ...fundingAccounts
                .filter((a) => a.programIds.includes(program.id))
                .map((a) => ({ label: a.name, tone: "sand" as const }))
        ];

        return (
            <ProgramActiveHeader
                name={program.name}
                statusLabel={statusLabelOf(program.status)}
                statusState={STATUS_STATE[program.status]}
                statusLabelClassName={STATUS_TEXT[program.status]}
                tags={tags}
                nextDistribution={program.nextDistribution ?? null}
            />
        );
    }

    // Draft (in_progress) — the editable name field + status badge. No tags,
    // no meter (the distribution cycle only exists once a program is active).
    return (
        <section className="py-4">
            <div className="px-4 flex items-start justify-between gap-8">
                <div className="min-w-0">
                    <div className="flex items-center gap-8">
                        <ProgramNameField
                            value={name}
                            onChange={onChangeName}
                        />
                        <StatusBadge
                            state={STATUS_STATE[program.status]}
                            label={statusLabelOf(program.status)}
                            labelClassName={STATUS_TEXT[program.status]}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

/** Program status → StatusBadge label. */
function statusLabelOf(status: Program["status"]): string {
    return status === "active"
        ? "Active"
        : status === "ended"
          ? "Ended"
          : "Draft";
}
